import type { LaserPointer } from "@/models";

export interface VideoBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Return the centered frame occupied by an aspect-fitted video inside its
 * element box. VideoPresenter fixes object-fit/object-position to these same
 * contain/center semantics.
 */
export function containedVideoBounds(
  bounds: VideoBounds,
  intrinsicWidth: number,
  intrinsicHeight: number,
): VideoBounds {
  if (
    bounds.width <= 0 ||
    bounds.height <= 0 ||
    intrinsicWidth <= 0 ||
    intrinsicHeight <= 0
  ) {
    return bounds;
  }

  const scale = Math.min(
    bounds.width / intrinsicWidth,
    bounds.height / intrinsicHeight,
  );
  const width = intrinsicWidth * scale;
  const height = intrinsicHeight * scale;

  return {
    left: bounds.left + (bounds.width - width) / 2,
    top: bounds.top + (bounds.height - height) / 2,
    width,
    height,
  };
}

/**
 * Convert a browser cursor position to the normalized coordinates used by
 * web and iOS presentation receivers.
 */
export function pointFromVideo(
  bounds: VideoBounds,
  clientX: number,
  clientY: number,
): LaserPointer | null {
  if (bounds.width <= 0 || bounds.height <= 0) return null;

  const x = (clientX - bounds.left) / bounds.width;
  const y = (clientY - bounds.top) / bounds.height;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;

  return { x, y };
}
